import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LOGO_URL } from "@/data/content";
import { API_BASE } from "@/lib/apiBase";

const API = API_BASE;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

const landingFor = (u) =>
  u.role === "admin" ? "/admin" : u.partner_id ? "/portal" : u.investor_stage || u.investor_access ? "/investors/portal" : "/account";

export default function Login({ investor = false }) {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to={landingFor(user)} replace />;

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (investor ? "/investors/portal" : "/account");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } =
        mode === "signin"
          ? await axios.post(`${API}/auth/login`, { email: form.email, password: form.password }, { withCredentials: true })
          : await axios.post(`${API}/auth/register`, form, { withCredentials: true });
      setUser(data);
      navigate(landingFor(data), { replace: true, state: { user: data } });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const valid = form.email.includes("@") && form.password.length >= (mode === "signup" ? 8 : 1) && (mode === "signin" || form.name.trim());

  return (
    <div data-testid={investor ? "investor-login-page" : "login-page"}>
      <Navbar />
      <main className="relative grid min-h-screen place-items-center bg-ink px-5 py-32">
        <div className="spotlight absolute inset-x-0 top-0 h-[420px]" />
        <div className="card-line relative w-full max-w-md rounded-2xl bg-white p-9 text-center sm:p-12">
          <img src={LOGO_URL} alt="Xtreme Exotics Watersports" className="mx-auto h-24 w-auto drop-shadow-[0_0_16px_rgba(0,252,245,0.3)]" />
          <h1 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-offwhite" data-testid="login-title">
            {investor ? "Investor Portal Sign-In" : "Partner Sign-In"}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            {investor
              ? "For approved investor access requests. Confidential preliminary materials — no funds are accepted."
              : "Sign in to your Partner Portal — or to track your Fleet Partner application."}
          </p>

          <div className="mt-7 space-y-3.5 text-left">
            {mode === "signup" && (
              <div>
                <label className="field-label" htmlFor="login-name">Full name</label>
                <input id="login-name" className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="login-name" />
              </div>
            )}
            <div>
              <label className="field-label" htmlFor="login-email">Email</label>
              <input id="login-email" type="email" className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" />
            </div>
            <div>
              <label className="field-label" htmlFor="login-password">Password{mode === "signup" && " (8+ characters)"}</label>
              <input id="login-password" type="password" className="field" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && valid && !busy && submit()} data-testid="login-password" />
            </div>
            <button onClick={submit} disabled={busy || !valid} className="btn-primary w-full disabled:opacity-50" data-testid="email-signin-btn">
              {busy ? <Loader2 size={15} className="animate-spin" /> : mode === "signin" ? "Sign In" : "Create Account"}
            </button>
            <button
              onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
              className="w-full text-center text-xs font-semibold text-muted transition-colors hover:text-aqua"
              data-testid="toggle-auth-mode"
            >
              {mode === "signin" ? "New here? Create an account" : "Already have an account? Sign in"}
            </button>
          </div>

          <div className="my-5 flex items-center gap-3" aria-hidden="true">
            <span className="h-px flex-1 bg-black/10" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted/60">or</span>
            <span className="h-px flex-1 bg-black/10" />
          </div>

          <button onClick={googleSignIn} className="btn-ghost w-full" data-testid="google-signin-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#0A0E10" d="M21.35 11.1H12v2.9h5.35c-.5 2.4-2.55 3.9-5.35 3.9a6 6 0 1 1 0-12c1.5 0 2.9.55 3.95 1.5l2.2-2.2A8.5 8.5 0 1 0 12 20.5c4.9 0 8.5-3.45 8.5-8.5 0-.3-.05-.6-.15-.9Z"/>
            </svg>
            Continue with Google
          </button>
          <p className="mt-5 text-[11px] leading-relaxed text-muted/70">
            Admin access is restricted to authorized Xtreme Exotics Watersports LLC accounts. Partner accounts are
            linked to your organization automatically when you use the email XE has on file.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
